import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ACTIONS } from "@/constants/roles";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { dashboardService } from "../services/dashboard.service";
import type {
  RentalSummaryDTO,
  RentalSearchItemDTO,
  CommissionSummaryItemDTO,
  DailyRentalData,
  RentalStatusCount,
  OverdueRental,
} from "../types/dashboard.types";

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function dayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAY_LABELS[dt.getDay()]} ${String(d).padStart(2, "0")}`;
}

function currentWeekRange(): { startDate: string; endDate: string } {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  return { startDate: toDateStr(monday), endDate: toDateStr(today) };
}

function buildDailyData(
  startDate: string,
  endDate: string,
  rentals: RentalSearchItemDTO[],
): DailyRentalData[] {
  const [sy, sm, sd] = startDate.split("-").map(Number);
  const [ey, em, ed] = endDate.split("-").map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const days: DailyRentalData[] = [];
  const cur = new Date(start);

  while (cur <= end) {
    const fullDate = toDateStr(cur);
    days.push({
      label: dayLabel(fullDate),
      fullDate,
      count: 0,
      revenue: 0,
      ticketMedio: 0,
    });
    cur.setDate(cur.getDate() + 1);
  }

  for (const rental of rentals) {
    const rentalDate = rental.rentedAt.slice(0, 10);
    const entry = days.find((d) => d.fullDate === rentalDate);
    if (entry) {
      entry.count++;
      entry.revenue += rental.totalValue;
    }
  }

  for (const day of days) {
    day.ticketMedio = day.count > 0 ? day.revenue / day.count : 0;
  }

  return days;
}

// Retorna true se o aluguel está atrasado: ON_STREET com expectedInAt anterior a hoje
function isOverdue(rental: RentalSearchItemDTO, todayStr: string): boolean {
  return (
    rental.status === "ON_STREET" &&
    !!rental.expectedInAt &&
    rental.expectedInAt.slice(0, 10) < todayStr
  );
}

export interface DashboardData {
  rentalSummary: RentalSummaryDTO | null;
  dailyData: DailyRentalData[];
  currentStatusCounts: RentalStatusCount[];
  commissions: CommissionSummaryItemDTO[];
  overdueRentals: OverdueRental[];
  maintenanceCount: number;
  totalRentals: number;
  completedRentals: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDashboardData(): DashboardData {
  const { isAdmin, hasPermission } = useAuth();

  const [rentalSummary, setRentalSummary] = useState<RentalSummaryDTO | null>(
    null,
  );
  const [activeSummary, setActiveSummary] = useState<RentalSummaryDTO | null>(
    null,
  );
  const [rentals, setRentals] = useState<RentalSearchItemDTO[]>([]);
  const [allOnStreet, setAllOnStreet] = useState<RentalSearchItemDTO[]>([]);
  const [commissions, setCommissions] = useState<CommissionSummaryItemDTO[]>(
    [],
  );
  const [maintenanceCount, setMaintenanceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { startDate, endDate } = useMemo(currentWeekRange, []);

  const canReadCommissions = hasPermission(ACTIONS.commissions.read);
  const canReadMaintenances = hasPermission(ACTIONS.maintenances.read);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [summary, rentalList, active, onStreetList] = await Promise.all([
        dashboardService.getRentalSummary(startDate, endDate),
        dashboardService.getRentals(startDate, endDate),
        dashboardService.getCurrentActiveSummary(),
        dashboardService.getAllOnStreetRentals(),
      ]);
      setRentalSummary(summary);
      setRentals(rentalList);
      setActiveSummary(active);
      setAllOnStreet(onStreetList);

      const extra: Promise<unknown>[] = [];

      if (canReadCommissions) {
        extra.push(
          dashboardService
            .getCommissionSummary(startDate, endDate)
            .then(setCommissions),
        );
      }

      if (canReadMaintenances) {
        extra.push(
          dashboardService
            .getMaintenanceCount(isAdmin)
            .then(setMaintenanceCount),
        );
      }

      await Promise.all(extra);
    } catch (error: unknown) {
      setError(
        getApiErrorMessage(error, "Erro ao carregar dados do dashboard."),
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, canReadCommissions, canReadMaintenances, isAdmin]);

  useEffect(() => {
    // loadData() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadData();
  }, [loadData]);

  const dailyData = useMemo(
    () => buildDailyData(startDate, endDate, rentals),
    [startDate, endDate, rentals],
  );

  const completedRentals = useMemo(
    () => rentals.filter((r) => r.status === "FINISHED").length,
    [rentals],
  );

  // Pie chart: usa todos os ON_STREET para separar em dia vs atrasado
  const currentStatusCounts = useMemo<RentalStatusCount[]>(() => {
    const todayStr = toDateStr(new Date());
    const overdueCount = allOnStreet.filter((r) =>
      isOverdue(r, todayStr),
    ).length;
    const onTimeCount = Math.max(0, allOnStreet.length - overdueCount);

    return [
      {
        status: "ON_STREET",
        label: "Na rua",
        count: onTimeCount,
        color: "#7c3aed",
      },
      {
        status: "OVERDUE",
        label: "Atrasado",
        count: overdueCount,
        color: "#dc2626",
      },
      {
        status: "INCOMPLETE",
        label: "Incompleto",
        count: activeSummary?.incompleteCount ?? 0,
        color: "#d97706",
      },
      {
        status: "FINISHED",
        label: "Finalizado (semana)",
        count: completedRentals,
        color: "#16a34a",
      },
    ].filter((s) => s.count > 0);
  }, [allOnStreet, activeSummary, completedRentals]);

  // Alertas: usa todos os ON_STREET (não apenas os da semana)
  const overdueRentals = useMemo<OverdueRental[]>(() => {
    const todayStr = toDateStr(new Date());
    return allOnStreet
      .filter((r) => isOverdue(r, todayStr))
      .map((r) => {
        const expectedDate = r.expectedInAt.slice(0, 10);
        const [ey, em, ed] = expectedDate.split("-").map(Number);
        const expected = new Date(ey, em - 1, ed);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysOverdue = Math.floor(
          (today.getTime() - expected.getTime()) / (1000 * 60 * 60 * 24),
        );
        return {
          idRental: r.idRental,
          clientName: r.clientName,
          expectedInAt: expectedDate,
          daysOverdue,
        };
      })
      .sort((a, b) => b.daysOverdue - a.daysOverdue)
      .slice(0, 5);
  }, [allOnStreet]);

  return {
    rentalSummary,
    dailyData,
    currentStatusCounts,
    commissions,
    overdueRentals,
    maintenanceCount,
    totalRentals: rentals.length,
    completedRentals,
    loading,
    error,
    refetch: loadData,
  };
}
